import Spinner from '$components/Spinner/Spinner';
import { useGetAdditionalDocumentsByCandidateIdQuery } from '$store/modules/additional-documents';
import { userLanguage } from '$translations/index';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Worker, Viewer, PdfJs } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { getFilePlugin } from '@react-pdf-viewer/get-file';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SimeraDark } from '$icons/simera-dark logo.svg';
/* Viewer component css */
import '@react-pdf-viewer/core/lib/styles/index.css';
/* react-pdf-viewer Zoom css */
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import { useGetCvQuery } from '$store/modules/uploads';
import { renderAsync } from 'docx-preview';
import { ReactComponent as DownloadIcon } from '../../icons/Download.svg';

// The viewer supports pdf.js 3 only, and pdf.js before 4.2.67 runs script hidden in a crafted font
// (CVE-2024-4367). Turning evaluation off is pdf.js's own mitigation; the worker must match it.
const PDFJS_VERSION = '3.11.174';
const withoutScriptEvaluation = (params: PdfJs.GetDocumentParams) => ({
  ...params,
  isEvalSupported: false,
});

const PreviewFile = ({
  providedUrl,
  hideBanner,
  closeHandler,
}: {
  providedUrl?: string;
  hideBanner?: boolean;
  closeHandler?: () => void;
}) => {
  const { fileId, candidateId, type } = useParams();
  const [t] = useTranslation('global');
  const [fileUrl, setFileUrl] = useState(providedUrl || '');
  const [fileExt, setFileExt] = useState(() => {
    if (!providedUrl) return '';

    const { pathname } = new URL(providedUrl);
    return pathname.slice(pathname.lastIndexOf('.') + 1); // get extension from file URL
  });
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomPopover, ZoomOutButton } = zoomPluginInstance;
  const getFilePluginInstance = getFilePlugin();
  const { Download } = getFilePluginInstance;
  const { isLoading: isLoadingFile, data: fileData } =
    useGetAdditionalDocumentsByCandidateIdQuery(
      {
        candidateId: parseInt(candidateId as string, 10),
        lang: userLanguage,
      },
      { skip: type !== 'file' || !fileId || !candidateId || !!providedUrl }
    );
  const { isLoading: isLoadingCV, data: cvData } = useGetCvQuery(
    {
      candidateId: parseInt(candidateId as string, 10),
    },
    { skip: !candidateId || type !== 'cv' || !!providedUrl }
  );

  const isLoading = isLoadingFile || isLoadingCV;
  const showError = !isLoading && !fileUrl;

  const renderError = () => {
    return (
      <div className="justify-center flex min-h-full">
        {t('modalDocumentPdf.error')}
      </div>
    );
  };

  useEffect(() => {
    let matchedUrl = '';
    let ext = '';
    if (type === 'file' && fileData) {
      const file = fileData?.find(
        ({ Id }) => Id === parseInt(fileId as string, 10)
      );
      matchedUrl = file?.Url || '';
      ext = file?.Key?.split('.')?.pop()?.toLowerCase() || '';
    }
    if (type === 'cv' && cvData) {
      matchedUrl = cvData?.Url;
      ext = cvData?.Key?.split('.')?.pop()?.toLowerCase() || '';
    }
    if (matchedUrl) {
      setFileUrl(matchedUrl);
      setFileExt(ext);
    }
  }, [cvData, fileData, fileId, setFileUrl, setFileExt, type]);

  // Render docx file using docx-preview
  useEffect(() => {
    if (fileExt === 'docx' && fileUrl && docxContainerRef.current) {
      docxContainerRef.current.innerHTML = '';
      fetch(fileUrl)
        .then((res) => res.arrayBuffer())
        .then((arrayBuffer) => {
          if (docxContainerRef.current) {
            renderAsync(arrayBuffer, docxContainerRef.current);
          }
        })
        .catch(() => {
          if (docxContainerRef.current) {
            docxContainerRef.current.innerHTML = t('modalDocumentPdf.error');
          }
        });
    }
  }, [fileExt, fileUrl, t]);

  const renderFileContent = () => {
    if (isLoading) {
      return <Spinner />;
    }

    if (showError) {
      return renderError();
    }

    if (fileExt === 'pdf') {
      return (
        <div className={`flex flex-col w-full ${!hideBanner && 'px-8'}`}>
          <Worker
            workerUrl={`https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`}
          >
            <div className="flex flex-col justify-center w-full items-center relative">
              <div className="flex flex-row gap-4 sticky z-50 top-4">
                <div className="flex flex-row rounded-full bg-gray-200 shadow overflow-hidden gap-4">
                  <ZoomOutButton />
                  <ZoomPopover />
                  <ZoomInButton />
                </div>
                <div className="flex flex-row rounded-full bg-gray-200 shadow overflow-hidden">
                  <Download />
                </div>
                {closeHandler && (
                  <button
                    type="button"
                    className="flex flex-row gap-2 rounded-full bg-gray-200 shadow overflow-hidden items-center py-1 px-2 text-slate-500 justify-center"
                    onClick={closeHandler}
                  >
                    <span>Close</span> <i className="pi pi-times" />
                  </button>
                )}
              </div>
              <Viewer
                fileUrl={fileUrl}
                transformGetDocumentParams={withoutScriptEvaluation}
                plugins={[zoomPluginInstance, getFilePluginInstance]}
                renderLoader={() => (
                  <div className="max-w-full max-h-full overflow-hidden flex items-center mt-32">
                    <Spinner height="100%" width="100%" />
                  </div>
                )}
              />
            </div>
          </Worker>
        </div>
      );
    }

    if (fileExt === 'docx') {
      return (
        <div className={`flex flex-col w-full ${!hideBanner && 'px-8'}`}>
          <div className="flex flex-row gap-4 sticky z-50 top-4 my-4">
            <a
              href={fileUrl}
              download
              className="flex flex-row rounded-full bg-gray-200 shadow overflow-hidden px-4 py-2 text-sm font-semibold hover:bg-gray-300"
              target="_blank"
              rel="noreferrer"
            >
              <DownloadIcon />
            </a>
          </div>
          <div
            ref={docxContainerRef}
            className="w-full bg-white border rounded shadow p-4 min-h-[400px]"
            style={{ minHeight: 400 }}
          />
        </div>
      );
    }

    return renderError();
  };

  return (
    <div className="min-h-screen">
      {!hideBanner && (
        <div className="flex justify-between items-center bg-white h-14 px-4 md:px-8">
          <SimeraDark className=" h-10" />
        </div>
      )}

      <div className="flex flex-1">{renderFileContent()}</div>
    </div>
  );
};

PreviewFile.defaultProps = {
  providedUrl: undefined,
  hideBanner: false,
  closeHandler: undefined,
};

export default PreviewFile;
